<template>
  <div class="export-view">
    <!-- ① 商品信息表单 -->
    <section class="card">
      <div class="card-head">
        <h3>商品信息</h3>
        <span class="hint">基本信息用于 pack 的 product.json</span>
      </div>
      <div class="form-grid">
        <label class="fld">
          <span class="fld-label">SKU <i class="req">*</i></span>
          <input v-model="form.sku" placeholder="例如 TEST-SWEATER-001" />
        </label>
        <label class="fld">
          <span class="fld-label">店铺账号 <i class="req">*</i></span>
          <input v-model="form.storeAccount" placeholder="例如 cyzp" />
        </label>
        <label class="fld">
          <span class="fld-label">商品类别 <i class="req">*</i></span>
          <select v-model="form.categoryKey">
            <option v-for="c in categories" :key="c.key" :value="c.key">{{ c.displayName }} → {{ c.productCategory }}</option>
          </select>
        </label>
        <label class="fld">
          <span class="fld-label">价格</span>
          <input v-model="form.price" placeholder="例如 39.9" />
        </label>
        <label class="fld">
          <span class="fld-label">库存数量</span>
          <input v-model="form.quantity" placeholder="例如 1000" />
        </label>
        <label class="fld">
          <span class="fld-label">品牌</span>
          <input v-model="form.brand" placeholder="例如 VANTYL" />
        </label>
        <label class="fld">
          <span class="fld-label">Parent SKU</span>
          <input v-model="form.parentSku" placeholder="留空则用 SKU 代替" />
        </label>
        <div class="fld">
          <span class="fld-label">售卖形式（自动检测）</span>
          <div class="readonly-value">
            <span class="tag" :class="isMultiVariant ? 'multi' : 'single'">{{ sellType }}</span>
            <span class="meta">{{ isMultiVariant ? `${colorCount} 个颜色` : '无颜色裂变' }}</span>
          </div>
        </div>
        <label class="fld fld-wide" v-if="isMultiVariant">
          <span class="fld-label">尺码列表（逗号分隔）</span>
          <input v-model="form.sizeList" placeholder="Small(小),Medium(中等),Large(大)" />
        </label>
      </div>
    </section>

    <!-- ② AI 属性分析 -->
    <section class="card">
      <div class="card-head">
        <h3>商品属性（AI 分析）</h3>
        <button class="btn btn-primary" :disabled="exp.analyzing" @click="onClickAnalyze">
          {{ exp.analyzing ? '分析中…' : '分析属性' }}
        </button>
      </div>
      <div v-if="exp.analyzing" class="loading-banner">
        <span class="spinner"></span>
        <span>AI 正在分析图片，请稍候…</span>
      </div>
      <div v-if="exp.analyzeError" class="error-banner">
        <strong>分析失败：</strong>{{ exp.analyzeError }}
      </div>
      <div v-if="analyzeSummary" class="success-banner">{{ analyzeSummary }}</div>
      <div class="attr-table">
        <div class="attr-row attr-head">
          <div class="col-key">字段</div>
          <div class="col-val">值</div>
          <div class="col-src">类型</div>
        </div>
        <div v-for="f in aiDerivableFields" :key="f.key" class="attr-row">
          <div class="col-key">
            <div class="key-name">{{ f.displayName }}</div>
            <div class="key-code">{{ f.key }}</div>
          </div>
          <div class="col-val">
            <input
              v-if="!isSelectField(f) && !isMultiCheckboxField(f)"
              v-model="analyzedFields[f.key]"
              :placeholder="f.defaultValue || '未分析'"
            />
            <select v-else-if="f.type === 'select'" v-model="analyzedFields[f.key]">
              <option value="">-- 未选择 --</option>
              <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
            </select>
            <div v-else class="radio-group">
              <label v-for="o in f.options" :key="o" class="radio-item">
                <input type="radio" :value="o" v-model="analyzedFields[f.key]" />
                <span>{{ o }}</span>
              </label>
            </div>
          </div>
          <div class="col-src">
            <span class="src-tag">{{ f.type }}</span>
            <span class="src-ai" v-if="hasFieldValue(f.key)">AI 已填</span>
          </div>
        </div>
        <div v-if="aiDerivableFields.length === 0" class="empty-row">请先选择商品类别</div>
      </div>
      <!-- 单独渲染 multi_checkbox（选项多，放表格里太挤） -->
      <div v-for="f in multiCheckboxFields" :key="f.key" class="checkbox-block">
        <div class="checkbox-head">
          <div class="key-name">{{ f.displayName }}</div>
          <div class="key-code">{{ f.key }}</div>
          <span class="src-tag">{{ f.type }}</span>
          <span class="src-ai" v-if="hasFieldValue(f.key)">AI 已填</span>
        </div>
        <div class="checkbox-group">
          <label v-for="o in f.options" :key="o" class="checkbox-item">
            <input
              type="checkbox"
              :value="o"
              :checked="hasOption(f.key, o)"
              @change="toggleOption(f.key, o)"
            />
            <span>{{ o }}</span>
          </label>
        </div>
      </div>
    </section>

    <!-- ③ 图片与文案映射 -->
    <section class="card">
      <div class="card-head">
        <h3>图片与文案映射</h3>
        <span class="hint">从产品 A 工作台自动读取</span>
      </div>

      <div v-if="isMultiVariant" class="image-block">
        <div class="block-title">多变种图片</div>
        <div v-for="v in imageMapping.variants" :key="v.vi" class="variant-row">
          <div class="variant-label">{{ v.colorLabel }}</div>
          <div class="variant-thumbs">
            <div class="thumb main">
              <img v-if="v.main" :src="v.main" />
              <span class="thumb-name">main_{{ v.vi + 1 }}.png</span>
            </div>
            <div class="thumb swatch">
              <img v-if="v.swatch" :src="v.swatch" />
              <span class="thumb-name">swatch_{{ v.vi + 1 }}.png</span>
            </div>
            <div v-for="(url, ri) in v.sub" :key="ri" class="thumb">
              <img :src="url" />
              <span class="thumb-name">sub_{{ v.vi + 1 }}_{{ ri + 1 }}.png</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="image-block">
        <div class="block-title">单品图片</div>
        <div class="variant-row">
          <div class="variant-label">主图</div>
          <div class="variant-thumbs">
            <div v-for="(img, i) in imageMapping.single" :key="i" class="thumb">
              <img :src="img.url" />
              <span class="thumb-name">main_{{ i + 1 }}.png</span>
            </div>
            <div v-if="imageMapping.single.length === 0" class="empty-row">无宣传图，请先到图片生成页生成</div>
          </div>
        </div>
      </div>

      <div class="copy-block" v-if="copyData">
        <div class="block-title">文案（自动从文案生成页读取）</div>
        <div class="copy-field"><span>标题</span><div>{{ copyData.title || '（空）' }}</div></div>
        <div class="copy-field"><span>卖点</span>
          <ul><li v-for="(f, i) in copyData.features" :key="i">{{ f }}</li></ul>
        </div>
      </div>
    </section>

    <!-- ④ 预览与下载 -->
    <section class="card">
      <div class="card-head">
        <h3>预览与下载</h3>
        <button class="btn btn-primary" :disabled="building" @click="onDownload">
          {{ building ? '打包中…' : '下载 pack ZIP' }}
        </button>
      </div>
      <details class="preview">
        <summary>查看 product.json 预览</summary>
        <pre>{{ previewJson }}</pre>
      </details>
      <div v-if="buildError" class="error-text">{{ buildError }}</div>
      <div class="tip">
        下载后将 ZIP 解压出的 <code>pack_{{ form.sku || 'SKU' }}/</code> 文件夹复制进店小秘
        <code>C:\Users\djjserver\Desktop\dianxiaomi\packs\</code>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useExportStore } from '@/stores/useExportStore'

const exp = useExportStore()
const {
  form, categories, sellType, isMultiVariant, colorCount,
  aiDerivableFields, analyzedFields, imageMapping, copyData,
  building, buildError
} = exp

const analyzeSummary = ref('')

const previewJson = computed(() => {
  try { return JSON.stringify(exp.buildProductJson(), null, 2) } catch (e) { return '// ' + e.message }
})

const isMultiCheckboxField = (f) => (f.type || '').toLowerCase() === 'multi_checkbox'
const isSelectField = (f) => ['select', 'radio'].includes((f.type || '').toLowerCase())

const multiCheckboxFields = computed(() =>
  (exp.aiDerivableFields || []).filter(isMultiCheckboxField)
)

const hasFieldValue = (key) => {
  const v = exp.analyzedFields ? exp.analyzedFields[key] : undefined
  if (v == null) return false
  if (Array.isArray(v)) return v.length > 0
  return String(v).trim() !== ''
}

const hasOption = (key, option) => {
  const v = exp.analyzedFields ? exp.analyzedFields[key] : undefined
  return Array.isArray(v) ? v.includes(option) : false
}

const toggleOption = (key, option) => {
  const cur = Array.isArray(exp.analyzedFields[key]) ? [...exp.analyzedFields[key]] : []
  const idx = cur.indexOf(option)
  if (idx >= 0) cur.splice(idx, 1)
  else cur.push(option)
  exp.analyzedFields[key] = cur
}

const onClickAnalyze = async () => {
  console.log('[ExportView] 分析属性 clicked')
  analyzeSummary.value = ''
  try {
    await exp.runAnalyze()
    const allFields = exp.analyzedFields || {}
    const filled = Object.values(allFields).filter(v =>
      (Array.isArray(v) ? v.length > 0 : String(v || '').trim() !== '')
    ).length
    const err = exp.analyzeError
    analyzeSummary.value = err ? '' : `AI 已填 ${filled} 个字段`
    console.log('[ExportView] analyze done', { filled, error: err })
  } catch (e) {
    console.error('[ExportView] analyze error', e)
  }
}

const onDownload = async () => {
  try { await exp.downloadPack() } catch (e) { console.error(e) }
}

onMounted(() => { exp.loadCategories() })
</script>

<style scoped>
.export-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: -28px;
}
.card {
  background: var(--bg-white);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: var(--shadow-sm);
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.card-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-title);
}
.card-head .hint {
  font-size: 12px;
  color: var(--text-sub);
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 16px;
}
.fld {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.fld-wide { grid-column: span 2; }
.fld-label {
  font-size: 12px;
  color: var(--text-sub);
  font-weight: 500;
}
.req { color: var(--danger); font-style: normal; }
.fld input, .fld select {
  padding: 6px 10px;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  font-size: 13.5px;
  background: var(--bg-white);
  color: var(--text-title);
  outline: none;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.fld input:focus, .fld select:focus {
  border-color: var(--primary-color);
}
.readonly-value {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}
.tag {
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}
.tag.multi { background: var(--primary-soft); color: var(--primary-active); }
.tag.single { background: var(--surface-soft); color: var(--text-body); }
.meta { font-size: 12px; color: var(--text-sub); }

.attr-table {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border-color);
}
.attr-row {
  display: grid;
  grid-template-columns: minmax(180px, 1.2fr) 2fr minmax(100px, 0.8fr);
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
}
.attr-head {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-sub);
  background: var(--surface-soft);
  padding: 8px 12px;
  margin: 0 -20px;
  border-bottom: 1px solid var(--border-color);
}
.attr-row:not(.attr-head) { padding: 0 12px; }
.key-name { font-size: 13px; font-weight: 500; color: var(--text-title); }
.key-code { font-size: 11px; color: var(--text-sub); font-family: monospace; }
.col-val input {
  width: 100%;
  padding: 5px 8px;
  border: 1px solid var(--border-strong);
  border-radius: 4px;
  font-size: 13px;
  background: var(--bg-white);
}
.col-src { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
.src-tag {
  font-size: 11px;
  color: var(--text-sub);
  background: var(--surface-soft);
  padding: 1px 6px;
  border-radius: 3px;
}
.src-ai {
  font-size: 11px;
  color: var(--primary-active);
  font-weight: 500;
}
.empty-row {
  padding: 16px;
  text-align: center;
  color: var(--text-sub);
  font-size: 13px;
}

.loading-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  margin-bottom: 10px;
  background: var(--primary-softer);
  border-radius: 6px;
  font-size: 13px;
  color: var(--primary-active);
}
.loading-banner .spinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--primary-soft);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-banner {
  padding: 8px 12px;
  margin-bottom: 10px;
  background: var(--danger-soft);
  color: var(--danger);
  border-radius: 6px;
  font-size: 13px;
  border-left: 3px solid var(--danger);
}

.success-banner {
  padding: 8px 12px;
  margin-bottom: 10px;
  background: var(--primary-softer);
  color: var(--primary-active);
  border-radius: 6px;
  font-size: 13px;
}

.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
}
.radio-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  color: var(--text-body);
  cursor: pointer;
}
.radio-item input { margin: 0; }

.checkbox-block {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--surface-soft);
}
.checkbox-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.checkbox-head .key-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-title);
}
.checkbox-head .key-code {
  font-size: 11px;
  color: var(--text-sub);
  font-family: monospace;
}
.checkbox-group {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 4px 12px;
}
.checkbox-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-body);
  cursor: pointer;
}
.checkbox-item input { margin: 0; }

.image-block, .copy-block {
  margin-top: 8px;
}
.block-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-title);
  margin-bottom: 8px;
}
.variant-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--border-color);
}
.variant-row:last-child { border-bottom: none; }
.variant-label {
  min-width: 80px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-body);
  padding-top: 6px;
}
.variant-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.thumb {
  position: relative;
  width: 70px;
}
.thumb img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  display: block;
}
.thumb.main img { border-color: var(--primary-color); }
.thumb.swatch img { border-color: var(--warning, #f59e0b); }
.thumb-name {
  display: block;
  font-size: 10px;
  color: var(--text-sub);
  margin-top: 2px;
  text-align: center;
  font-family: monospace;
}

.copy-field {
  display: flex;
  gap: 12px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px dashed var(--border-color);
}
.copy-field:last-child { border-bottom: none; }
.copy-field span {
  min-width: 60px;
  color: var(--text-sub);
  font-weight: 500;
}
.copy-field ul {
  margin: 0;
  padding-left: 16px;
  color: var(--text-title);
}

.btn {
  padding: 7px 18px;
  border-radius: var(--radius-sm);
  border: none;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
}
.btn-primary {
  background: var(--primary-gradient);
  color: #fff;
  box-shadow: var(--shadow-primary);
}
.btn-primary:hover:not(:disabled) {
  box-shadow: var(--shadow-primary-hover);
  transform: translateY(-1px);
}
.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.preview {
  margin-top: 8px;
  background: var(--surface-soft);
  border-radius: 6px;
  padding: 8px 12px;
}
.preview summary {
  cursor: pointer;
  font-size: 13px;
  color: var(--text-body);
  font-weight: 500;
}
.preview pre {
  margin: 8px 0 0;
  font-size: 11.5px;
  font-family: monospace;
  color: var(--text-title);
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 320px;
  overflow-y: auto;
}

.error-text {
  margin-top: 8px;
  padding: 6px 10px;
  background: var(--danger-soft);
  color: var(--danger);
  border-radius: 4px;
  font-size: 12.5px;
}

.tip {
  margin-top: 10px;
  padding: 8px 12px;
  background: var(--primary-softer);
  border-radius: 6px;
  font-size: 12px;
  color: var(--text-body);
  line-height: 1.6;
}
.tip code {
  background: var(--bg-white);
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11.5px;
  color: var(--text-title);
}

@media (max-width: 720px) {
  .form-grid { grid-template-columns: 1fr; }
  .fld-wide { grid-column: span 1; }
  .attr-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
